import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { useCurrentUnit } from './useCurrentUnit.js'

// Drives the Daily Huddle screen — the manager's 30-second morning view.
// Pulls only what the screen renders. Each query is small and bounded
// so a fresh load is fast even on a phone over cellular.
//
// Returns the raw inputs (todayWasteTotal, criticalItems, etc.) so the
// screen can compose its own summary sentence and metric cards.
export function useHuddleData() {
  const [todayLogs, setTodayLogs]               = useState([])
  const [yesterdayTotal, setYesterdayTotal]     = useState(0)
  const [savingsSummary, setSavingsSummary]     = useState({
    confirmed_total: 0, preliminary_total: 0, lifetime_total: 0,
    confirmed_count: 0, event_count: 0, today_total: 0, last_event_source: null,
  })
  const [items, setItems]                       = useState([])
  const [todayReceiveLogs, setTodayReceiveLogs] = useState([])
  const [settings, setSettings]                 = useState({
    daily_target: 50, large_entry_threshold: 50,
  })
  // Today's roster — schedule_assignments rows for today's date,
  // joined to the staff record so we have wage info for labor
  // calculations without a second query.
  const [todayAssignments, setTodayAssignments] = useState([])
  const [activeStaff, setActiveStaff]           = useState([])
  // Today's sales row (from imported CSV, if any). Drives the
  // "X% of sales" subtitle on the Today's Waste card.
  const [todaySales, setTodaySales]             = useState(null)
  // 7-day rolling totals for the prime cost synthesis card. We
  // store the raw arrays so memoized derivations stay reactive.
  const [sales7d, setSales7d]                   = useState([])
  const [receivings7d, setReceivings7d]         = useState([])
  const [assignments7d, setAssignments7d]       = useState([])
  const [loading, setLoading]                   = useState(true)
  const [error, setError]                       = useState(null)

  // Multi-unit scope. null for single-unit accounts → queries below
  // stay byte-identical (RLS membership scoping, no restaurant filter).
  // Non-null for a grouped user with a unit selected → scope each
  // tenant query to that unit so a multi-membership RLS merge can't
  // bleed other units' rows into the Huddle.
  const { currentRestaurantId } = useCurrentUnit()

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)

    // Apply the unit filter only when grouped. For single-unit (null)
    // the query is returned untouched — byte-identical behavior.
    const scope = (q) => (currentRestaurantId ? q.eq('restaurant_id', currentRestaurantId) : q)

    // Fresh kitchen-day boundaries every fetch (same pattern as the
    // other data hooks — see useWasteData for rationale).
    const now = new Date()
    const todayStart = new Date(
      now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0
    )
    const yestStart = new Date(todayStart)
    yestStart.setDate(yestStart.getDate() - 1)
    const startOfToday     = todayStart.toISOString()
    const startOfYesterday = yestStart.toISOString()

    // Today's date in YYYY-MM-DD — used to filter schedule_assignments.
    // Use browser-local time, same convention as the kitchen-day boundaries
    // above. (Phase 2C will pin this to restaurant.timezone explicitly.)
    const todayDateStr = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      .toLocaleDateString('en-CA') // YYYY-MM-DD

    // Last-7-days window for the rolling prime cost calc. Includes
    // today as day 7. Daily prime cost is too noisy to be useful
    // (deliveries don't happen every day, labor varies by shift) —
    // the 7-day rolling is what professional restaurant operators
    // actually watch and what the Huddle synthesizes below the grid.
    const sevenDaysAgo = new Date(todayStart)
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6) // includes today, so -6 = 7-day window
    const startOf7d        = sevenDaysAgo.toISOString()
    const startOf7dDateStr = sevenDaysAgo.toLocaleDateString('en-CA')

    try {
      const [
        wasteTodayRes, wasteYestRes, savingsRes,
        itemsRes, recvTodayRes, settingsRes,
        assignmentsRes, staffRes, todaySalesRes,
        sales7dRes, receiv7dRes, assign7dRes,
      ] = await Promise.all([
        // Today's waste logs — full rows so we can rank top wasted item
        scope(supabase.from('waste_logs')
          .select('total_cost, item_name, quantity, unit, reason')
          .gte('logged_at', startOfToday)
          .order('total_cost', { ascending: false })),
        // Yesterday's totals only — we just need the sum
        scope(supabase.from('waste_logs')
          .select('total_cost')
          .gte('logged_at', startOfYesterday)
          .lt('logged_at', startOfToday)),
        // Lifetime savings summary. This is an aggregate VIEW with no
        // restaurant_id column, so it can't be unit-scoped here — a
        // grouped user sees merged savings totals until Ship 2 reworks
        // the view. Single-unit is unaffected (RLS returns one unit).
        supabase.from('vw_savings_summary').select('*').single(),
        // Active items with par data — for critical inventory list
        scope(supabase.from('items')
          .select('id, name, unit, par_qty, current_on_hand, cost_per_unit')
          .eq('active', true)
          .gt('par_qty', 0)
          .order('name')),
        // Today's deliveries — count + total
        scope(supabase.from('receive_logs')
          .select('total_cost, supplier_name')
          .gte('logged_at', startOfToday)),
        // Daily target for the summary sentence baseline. RLS scopes
        // to the user's restaurant — no explicit filter needed.
        scope(supabase.from('settings')
          .select('daily_target, large_entry_threshold')
          .maybeSingle()),
        // Today's roster + joined staff so we have wages for labor calc.
        // 'called_out' / 'no_show' shifts are still included so the
        // manager sees them; they just don't count toward labor cost.
        scope(supabase.from('schedule_assignments')
          .select(`
            id, shift_date, start_time, end_time, role, status, notes,
            staff:staff_id(id, name, role, hourly_wage)
          `)
          .eq('shift_date', todayDateStr)
          .order('start_time')),
        // Active staff list for the "Add to roster" picker
        scope(supabase.from('staff')
          .select('id, name, role, hourly_wage')
          .eq('active', true)
          .order('name')),
        // Today's sales row from the imported CSV (if any). Used for
        // waste-as-%-of-sales on the Huddle. maybeSingle so missing
        // sales data is a non-error.
        scope(supabase.from('daily_sales')
          .select('sale_date, gross_sales, net_sales, transaction_count')
          .eq('sale_date', todayDateStr)
          .maybeSingle()),
        // Last 7 days of sales — for the rolling prime cost calc
        // and so the Huddle can show "X% of sales" against a less
        // noisy denominator than today alone.
        scope(supabase.from('daily_sales')
          .select('sale_date, gross_sales, net_sales')
          .gte('sale_date', startOf7dDateStr)
          .lte('sale_date', todayDateStr)),
        // Last 7 days of receivings — sums to the COGS-numerator side
        // of the prime cost calc.
        scope(supabase.from('receive_logs')
          .select('total_cost, logged_at')
          .gte('logged_at', startOf7d)),
        // Last 7 days of schedule assignments + staff for the labor side.
        // 'called_out' / 'no_show' / 'cancelled' shifts don't count
        // toward labor cost (matches the per-day calc on the Roster).
        scope(supabase.from('schedule_assignments')
          .select(`
            shift_date, start_time, end_time, status,
            staff:staff_id(hourly_wage)
          `)
          .gte('shift_date', startOf7dDateStr)
          .lte('shift_date', todayDateStr)),
      ])

      if (wasteTodayRes.error) throw wasteTodayRes.error
      if (wasteYestRes.error)  throw wasteYestRes.error
      if (itemsRes.error)      throw itemsRes.error
      if (recvTodayRes.error)  throw recvTodayRes.error
      // savings/settings/assignments/staff can fail silently — defaults

      setTodayLogs(wasteTodayRes.data ?? [])
      setYesterdayTotal(
        (wasteYestRes.data ?? []).reduce(
          (s, r) => s + Number(r.total_cost || 0), 0
        )
      )
      if (savingsRes.data) setSavingsSummary(savingsRes.data)
      setItems(itemsRes.data ?? [])
      setTodayReceiveLogs(recvTodayRes.data ?? [])
      if (settingsRes.data) setSettings(settingsRes.data)
      setTodayAssignments(assignmentsRes.data ?? [])
      setActiveStaff(staffRes.data ?? [])
      setTodaySales(todaySalesRes?.data ?? null)
      setSales7d(sales7dRes?.data ?? [])
      setReceivings7d(receiv7dRes?.data ?? [])
      setAssignments7d(assign7dRes?.data ?? [])
    } catch (e) {
      setError(e)
      // eslint-disable-next-line no-console
      console.error('[useHuddleData]', e)
    } finally {
      setLoading(false)
    }
  }, [currentRestaurantId])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Midnight rollover + visibility refresh, same pattern as other hooks.
  useEffect(() => {
    let lastDate = new Date().toDateString()
    const id = setInterval(() => {
      const today = new Date().toDateString()
      if (today !== lastDate) {
        lastDate = today
        fetchAll()
      }
    }, 60_000)
    return () => clearInterval(id)
  }, [fetchAll])

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') fetchAll()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [fetchAll])

  // Today's waste total
  const todayWasteTotal = useMemo(
    () => todayLogs.reduce((s, l) => s + Number(l.total_cost || 0), 0),
    [todayLogs]
  )

  // Waste as a percentage of today's net sales — only meaningful if
  // sales data was imported AND there was real revenue today. Returns
  // null otherwise so the UI can show a fallback subtitle instead.
  // Prefer net over gross because net is the closer comp for "money
  // left over after running the kitchen".
  const wastePctOfSales = useMemo(() => {
    if (!todaySales) return null
    const net = Number(todaySales.net_sales ?? 0)
    const gross = Number(todaySales.gross_sales ?? 0)
    const denom = net > 0 ? net : (gross > 0 ? gross : 0)
    if (denom <= 0) return null
    return (todayWasteTotal / denom) * 100
  }, [todaySales, todayWasteTotal])

  // Today's sales denominator helper — used by the labor% and
  // receivings% calcs so they all use the same fallback rule.
  function todaySalesDenominator(salesRow) {
    if (!salesRow) return 0
    const net   = Number(salesRow.net_sales   ?? 0)
    const gross = Number(salesRow.gross_sales ?? 0)
    return net > 0 ? net : (gross > 0 ? gross : 0)
  }

  // Today's receive total + count
  const todayReceiveTotal = useMemo(
    () => todayReceiveLogs.reduce(
      (s, l) => s + Number(l.total_cost || 0), 0
    ),
    [todayReceiveLogs]
  )
  const todayReceiveCount = todayReceiveLogs.length
  const todaySupplierCount = useMemo(() => {
    const set = new Set()
    for (const l of todayReceiveLogs) {
      if (l.supplier_name) set.add(l.supplier_name)
    }
    return set.size
  }, [todayReceiveLogs])

  // Items needing reorder + restock cost
  const itemsNeedingReorder = useMemo(
    () => items.filter(
      (i) => Number(i.current_on_hand) < Number(i.par_qty)
    ),
    [items]
  )

  const restockCost = useMemo(
    () => itemsNeedingReorder.reduce((s, i) => {
      const need = Math.max(0, Number(i.par_qty) - Number(i.current_on_hand))
      return s + need * Number(i.cost_per_unit)
    }, 0),
    [itemsNeedingReorder]
  )

  // Top-5 most-critical items (lowest pct of par remaining).
  // Anchors the Critical Inventory section on the screen.
  const criticalItems = useMemo(() => {
    return itemsNeedingReorder
      .map((i) => ({
        ...i,
        pctRemaining:
          Number(i.par_qty) > 0
            ? (Number(i.current_on_hand) / Number(i.par_qty)) * 100
            : 0,
      }))
      .sort((a, b) => a.pctRemaining - b.pctRemaining)
      .slice(0, 5)
  }, [itemsNeedingReorder])

  // ---------- Today's labor calculation -------------------
  // Hours for one assignment, accounting for end_time wrapping past
  // midnight (e.g. closing shift 5pm-2am). Treats negative diff as
  // a wrap, adds 24h.
  function hoursBetween(start, end) {
    if (!start || !end) return 0
    const [sh, sm] = start.split(':').map(Number)
    const [eh, em] = end.split(':').map(Number)
    const startMin = sh * 60 + sm
    let endMin   = eh * 60 + em
    if (endMin < startMin) endMin += 24 * 60   // wraps past midnight
    return Math.max(0, (endMin - startMin) / 60)
  }

  const todayLabor = useMemo(() => {
    let cost = 0
    let hours = 0
    let countingAssignments = 0
    const staffSet = new Set()
    for (const a of todayAssignments) {
      // 'called_out' and 'no_show' don't count toward cost, but the
      // shift still appears in the roster (manager sees it).
      const counts = a.status === 'scheduled' || a.status === 'completed'
      const h = hoursBetween(a.start_time, a.end_time)
      if (counts) {
        const wage = Number(a.staff?.hourly_wage || 0)
        cost += h * wage
        hours += h
        countingAssignments++
        if (a.staff?.id) staffSet.add(a.staff.id)
      }
    }
    return {
      cost,
      hours,
      assignment_count: countingAssignments,
      total_assignments: todayAssignments.length,
      staff_count: staffSet.size,
    }
  }, [todayAssignments])

  // Today's labor as % of today's sales — same null-fallback rule
  // as wastePctOfSales (no sales data → no percentage shown).
  const laborPctOfSales = useMemo(() => {
    const denom = todaySalesDenominator(todaySales)
    if (denom <= 0) return null
    return (todayLabor.cost / denom) * 100
  }, [todaySales, todayLabor])

  // Today's receivings as % of today's sales. Caveat in the UI: this
  // is *spiky* because deliveries don't happen every day. The 7-day
  // rolling number below is the more meaningful one for cost control.
  const receivingsPctOfSales = useMemo(() => {
    const denom = todaySalesDenominator(todaySales)
    if (denom <= 0) return null
    return (todayReceiveTotal / denom) * 100
  }, [todaySales, todayReceiveTotal])

  // 7-day rolling totals + prime cost — the headline cost-control
  // metric most restaurant operators watch. Prime cost = labor + COGS.
  // Industry healthy benchmarks:
  //   Food cost: 28-32% of sales
  //   Labor:     25-30% of sales
  //   Prime:     55-60% of sales (the combined target)
  // Above 65% is a real margin concern; below 55% on a steady-state
  // basis is excellent.
  const primeCost7d = useMemo(() => {
    // Sales denominator over the window. Prefer net, fall back to gross.
    let salesNet = 0, salesGross = 0
    for (const s of sales7d) {
      salesNet   += Number(s.net_sales   || 0)
      salesGross += Number(s.gross_sales || 0)
    }
    const salesTotal = salesNet > 0 ? salesNet : salesGross

    // Receivings sum (no status filter — deliveries are deliveries).
    let receivingsTotal = 0
    for (const r of receivings7d) {
      receivingsTotal += Number(r.total_cost || 0)
    }

    // Labor sum (only counted statuses contribute to cost).
    let laborTotal = 0
    for (const a of assignments7d) {
      if (a.status !== 'scheduled' && a.status !== 'completed') continue
      const h = hoursBetween(a.start_time, a.end_time)
      const wage = Number(a.staff?.hourly_wage || 0)
      laborTotal += h * wage
    }

    if (salesTotal <= 0) {
      // No sales imported for the window — return the dollar amounts
      // but no percentages so the UI can fall back gracefully.
      return {
        sales: salesTotal,
        receivings: receivingsTotal,
        labor: laborTotal,
        cogs_pct: null,
        labor_pct: null,
        prime_pct: null,
        days_with_sales: 0,
      }
    }

    const days_with_sales = sales7d.filter(
      (s) => Number(s.net_sales || s.gross_sales || 0) > 0
    ).length

    return {
      sales:           salesTotal,
      receivings:      receivingsTotal,
      labor:           laborTotal,
      cogs_pct:        (receivingsTotal / salesTotal) * 100,
      labor_pct:       (laborTotal / salesTotal) * 100,
      prime_pct:       ((receivingsTotal + laborTotal) / salesTotal) * 100,
      days_with_sales,
    }
  }, [sales7d, receivings7d, assignments7d])

  // Mutations for the roster — used by the Add-to-roster modal and
  // the row-level remove action.
  const createAssignment = useCallback(async (payload) => {
    // Single-unit (null): omit restaurant_id, the 0019 default trigger
    // fills it — byte-identical. Grouped: set it explicitly so the
    // shift lands on the SELECTED unit, not the trigger's first-membership.
    const { data, error: insErr } = await supabase
      .from('schedule_assignments')
      .insert({
        staff_id:   payload.staff_id,
        shift_date: payload.shift_date,
        start_time: payload.start_time,
        end_time:   payload.end_time,
        role:       payload.role || null,
        notes:      payload.notes || null,
        ...(currentRestaurantId ? { restaurant_id: currentRestaurantId } : {}),
      })
      .select()
      .single()
    if (insErr) throw insErr
    await fetchAll()
    return data
  }, [fetchAll, currentRestaurantId])

  const deleteAssignment = useCallback(async (id) => {
    const { error: delErr } = await supabase
      .from('schedule_assignments')
      .delete()
      .eq('id', id)
    if (delErr) throw delErr
    await fetchAll()
  }, [fetchAll])

  // Top-1 wasted item today (drives the summary sentence).
  const topWastedToday = useMemo(() => {
    const map = new Map()
    for (const l of todayLogs) {
      const prev = map.get(l.item_name) || { name: l.item_name, cost: 0 }
      map.set(l.item_name, {
        ...prev,
        cost: prev.cost + Number(l.total_cost || 0),
      })
    }
    return [...map.values()].sort((a, b) => b.cost - a.cost)[0] ?? null
  }, [todayLogs])

  return {
    settings,
    todayWasteTotal,
    yesterdayTotal,
    savingsSummary,
    itemsNeedingReorder,
    criticalItems,
    restockCost,
    todayReceiveTotal,
    todayReceiveCount,
    todaySupplierCount,
    topWastedToday,
    todayAssignments,
    activeStaff,
    todayLabor,
    todaySales,
    wastePctOfSales,
    laborPctOfSales,
    receivingsPctOfSales,
    primeCost7d,
    // Item count exposed for the marketing nudge gate (only show
    // the "try AI drafts" card once the menu is populated enough
    // that the AI has real data to draft from).
    itemCount: items.length,
    loading,
    error,
    refresh: fetchAll,
    createAssignment,
    deleteAssignment,
  }
}
